import { Howl } from 'howler'
import { useState } from 'react'

import useDebugger from './useDebugger'

function useQueueSound() {
  const { debug } = useDebugger()

  const [isPlaying, setIsPlaying] = useState(false)

  const playSound = (filename: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const sound = new Howl({
        src: [`/audio/${filename}`],
        onend: () => resolve(),
        onloaderror: (_soundId, error) => {
          debug('error', 'Error playing audio:', error)
          reject(error)
        },
      })

      sound.play()
    })
  }

  const splitIntoComponents = (number: number) => {
    const hundreds = Math.floor(number / 100)
    const tens = Math.floor((number - hundreds * 100) / 10)
    const ones = number - hundreds * 100 - tens * 10
    return { hundreds, tens, ones }
  }

  const playAlphanumericSound = async (alphanumeric: string): Promise<void> => {
    for (const char of alphanumeric) {
      if (/[a-z]/i.test(char)) {
        await playSound(`${char.toLowerCase()}.wav`)
      }
    }

    const numberPart = parseInt(alphanumeric.match(/\d+/)?.[0] || '0', 10)
    if (numberPart) {
      await playNumberSound(numberPart)
    }
  }

  const playNumberSound = async (number: number): Promise<void> => {
    const { hundreds, tens, ones } = splitIntoComponents(number)

    if (hundreds !== 0) {
      await playSound(`${hundreds * 100}.wav`)
    }

    const lastTwoDigits = tens * 10 + ones
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      await playSound(`${lastTwoDigits}.wav`)
      return
    }

    if (tens !== 0) await playSound(`${tens * 10}.wav`)
    if (ones !== 0) await playSound(`${ones}.wav`)
  }

  const playQueueSound = async (ticketNumber: string, counterString: string): Promise<void> => {
    setIsPlaying(true)

    try {
      await playSound('antrian.wav')
      await playAlphanumericSound(ticketNumber)
      await playSound('counter.wav')
      await playAlphanumericSound(counterString)
    } catch (error) {
      debug('error', 'Error while playing queue sound:', error)
    } finally {
      setIsPlaying(false)
    }
  }

  return { isPlaying, playQueueSound }
}

export default useQueueSound
